import React, { useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { PermissionWrapper } from "../../../components/PermissionWrapper/PermissionWrapper";
import { StatCard } from "../../../components/Ads/StatCard/StatCard";
import { BudgetAlertCard } from "./components/BudgetAlertCard";
import { ExpiringCampaignCard } from "./components/ExpiringCampaignCard";
import { useAdsOverview } from "../hooks";

import "./Overview.css";

function deltaTone(pctChange: number): "positive" | "negative" | "neutral" {
  if (pctChange > 0) return "positive";
  if (pctChange < 0) return "negative";
  return "neutral";
}

function deltaText(pctChange: number): string {
  return `${Math.abs(pctChange)}% from last month`;
}

const OverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { values: { overview, error }, loading, exporting, functions } = useAdsOverview();

  const handleCreateCampaign = useCallback(() => {
    navigate("/ads/campaigns/create");
  }, [navigate]);

  const handleExportReport = useCallback(async () => {
    try {
      const blob = await functions.exportReport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ads-overview-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      // error surfaced via the error banner
    }
  }, [functions]);

  const stats = overview?.stats;

  const statCards = [
    {
      title: "Active Campaigns",
      value: loading ? "—" : String(stats?.activeCampaigns.value ?? 0),
      icon: "ph-megaphone",
      deltaText: deltaText(stats?.activeCampaigns.pctChange ?? 0),
      deltaTone: deltaTone(stats?.activeCampaigns.pctChange ?? 0),
    },
    {
      title: "Total Revenue (MTD)",
      value: loading ? "—" : "₹" + (stats?.totalRevenueMTD.value ?? 0).toLocaleString("en-IN"),
      icon: "ph-currency-inr",
      deltaText: deltaText(stats?.totalRevenueMTD.pctChange ?? 0),
      deltaTone: deltaTone(stats?.totalRevenueMTD.pctChange ?? 0),
    },
    {
      title: "Impressions (Today)",
      value: loading ? "—" : (stats?.impressionsToday.value ?? 0).toLocaleString("en-IN"),
      icon: "ph-eye",
      deltaText: deltaText(stats?.impressionsToday.pctChange ?? 0),
      deltaTone: deltaTone(stats?.impressionsToday.pctChange ?? 0),
    },
    {
      title: "Clicks (Today)",
      value: loading ? "—" : (stats?.clicksToday.value ?? 0).toLocaleString("en-IN"),
      icon: "ph-hand-pointing",
      deltaText: deltaText(stats?.clicksToday.pctChange ?? 0),
      deltaTone: deltaTone(stats?.clicksToday.pctChange ?? 0),
    },
    {
      title: "CTR",
      value: loading ? "—" : (stats?.ctrToday.value ?? 0) + "%",
      icon: "ph-target",
      deltaText: deltaText(stats?.ctrToday.pctChange ?? 0),
      deltaTone: deltaTone(stats?.ctrToday.pctChange ?? 0),
    },
    {
      title: "Active Popups",
      value: loading ? "—" : String(stats?.activePopups.value ?? 0),
      icon: "ph-monitor-play",
      deltaText: deltaText(stats?.activePopups.pctChange ?? 0),
      deltaTone: deltaTone(stats?.activePopups.pctChange ?? 0),
    },
  ];

  const budgetAlerts = overview?.budgetAlerts ?? [];
  const expiringCampaigns = overview?.expiringCampaigns ?? [];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="ads-overview__header-row">
          <div className="ads-overview__title-block">
            <h1 className="ads-overview__title">Overview</h1>
            <p className="ads-overview__subtitle">Operational overview — March 2, 2026</p>
          </div>

          <div className="ads-overview__actions">
            <PermissionWrapper anyOfPermissions={["ads.admin", "ads.manage"]} hideWhenDisabled>
              <button type="button" className="ads-overview__action-btn ads-overview__action-btn--primary" onClick={handleCreateCampaign}>
                <i className="ph-plus ads-overview__btn-icon" aria-hidden="true" />
                Create Campaign
              </button>
            </PermissionWrapper>

            <PermissionWrapper anyOfPermissions={["ads.admin", "ads.manage"]} hideWhenDisabled>
              <button
                type="button"
                className="ads-overview__action-btn ads-overview__action-btn--outline"
                onClick={handleExportReport}
                disabled={exporting}
              >
                <i className={`${exporting ? "ph-spinner" : "ph-download-simple"} ads-overview__btn-icon`} aria-hidden="true" />
                {exporting ? "Exporting…" : "Export Report"}
              </button>
            </PermissionWrapper>
          </div>
        </div>

        {error && (
          <div className="ads-overview__error-banner">
            <i className="ph-warning-circle" aria-hidden="true" /> {error}
          </div>
        )}

        <Row className="g-3 mt-1">
          {statCards.map((c) => (
            <Col key={c.title} xs={12} sm={6} lg={4}>
              <StatCard
                title={c.title}
                value={c.value}
                iconClassName={c.icon}
                deltaText={c.deltaText}
                deltaTone={c.deltaTone}
              />
            </Col>
          ))}
        </Row>

        <Row className="mt-3 g-3">
          <Col xs={12} lg={6}>
            <div className="ads-panel">
              <div className="ads-panel__header">
                <i className="ph-warning ads-panel__header-icon" aria-hidden="true" />
                <span className="ads-panel__header-title">Budget Alerts</span>
              </div>
              <div className="ads-panel__body">
                {loading && <p className="ads-panel__empty">Loading…</p>}
                {!loading && budgetAlerts.length === 0 && (
                  <p className="ads-panel__empty">No budget alerts at this time.</p>
                )}
                {!loading && budgetAlerts.map((b, i) => (
                  <BudgetAlertCard
                    key={i}
                    name={b.name}
                    advertiser={b.advertiser}
                    spent={b.spent}
                    budget={b.budget}
                    pct={b.pct}
                  />
                ))}
              </div>
            </div>
          </Col>

          <Col xs={12} lg={6}>
            <div className="ads-panel">
              <div className="ads-panel__header">
                <i className="ph-clock ads-panel__header-icon" aria-hidden="true" />
                <span className="ads-panel__header-title">Campaigns Expiring Soon</span>
              </div>
              <div className="ads-panel__body">
                {loading && <p className="ads-panel__empty">Loading…</p>}
                {!loading && expiringCampaigns.length === 0 && (
                  <p className="ads-panel__empty">No campaigns expiring soon.</p>
                )}
                {!loading && expiringCampaigns.map((c, i) => (
                  <ExpiringCampaignCard
                    key={i}
                    name={c.name}
                    advertiser={c.advertiser}
                    daysLeft={c.daysLeft}
                    endsDate={c.endsDate}
                    urgent={c.urgent}
                  />
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OverviewPage;
